/**
 * `preferred_language` → BCP-47 normalizer for the translator rail (AC-TRX-1).
 *
 * The rail's `patientLanguage` prop must be a tag the @astrana/ui `Translator`
 * roster (`TRANSLATOR_LANGUAGES`) actually contains — anything else silently
 * degrades to the component's own default, or worse, is handed to a vendor that
 * rejects it mid-session. This module is the single seam that guarantees a
 * roster tag comes out.
 *
 * Why a LOCAL table instead of importing `TRANSLATOR_LANGUAGES`:
 * the `Translator` component is not in the pinned `@astrana/ui` release yet
 * (v0.4.44 has no such export), so importing the roster here would not compile.
 * The `ROSTER` below is transcribed verbatim (code + English label) from
 * `src/components/modules/Translator/translatorConfig.ts` on the upstream
 * `translator-rail` branch.
 *
 * TODO(AC-TRX-1, pin bump / Task C5): once the pin carries `Translator`, assert
 * this table against the real roster — i.e. verify every `code` here is present
 * in `TRANSLATOR_LANGUAGES` — rather than trusting the transcription. The
 * mapping deliberately stays local (the rail's roster is a UI concern; the
 * clinical vocabulary this normalizes FROM is ours), but it must not drift.
 *
 * The upstream vocabulary of care-gql's `preferred_language` is NOT confirmed —
 * it may be a free-text English label ("Spanish"), an ISO code ("es"), a
 * regional variant ("es-MX"), or arbitrary case. All of those are handled;
 * anything unrecognised (including null/blank) falls back to Spanish, by far
 * the dominant LEP language in the service area and the component's own
 * `DEFAULT_PATIENT_LANGUAGE`.
 */

/** Fallback tag — matches the component's `DEFAULT_PATIENT_LANGUAGE`. */
export const DEFAULT_PATIENT_LANGUAGE = "es";

/**
 * `TRANSLATOR_LANGUAGES` transcribed as `[canonical tag, English label]`, in
 * the upstream file's order. Keep in sync with that file (see module doc).
 */
const ROSTER: ReadonlyArray<readonly [code: string, label: string]> = [
  ["en", "English"],
  ["es", "Spanish"],
  ["cmn", "Mandarin"],
  ["yue", "Cantonese"],
  ["ko", "Korean"],
  ["ja", "Japanese"],
  ["tl", "Tagalog"],
  ["vi", "Vietnamese"],
  ["th", "Thai"],
  ["km", "Khmer"],
  ["hi", "Hindi"],
  ["pa", "Punjabi"],
  ["ur", "Urdu"],
  ["bn", "Bengali"],
  ["gu", "Gujarati"],
  ["ta", "Tamil"],
  ["te", "Telugu"],
  ["ml", "Malayalam"],
  ["ne", "Nepali"],
  ["fa", "Farsi"],
  ["ar", "Arabic"],
  ["hy", "Armenian"],
  ["he", "Hebrew"],
  ["tr", "Turkish"],
  ["fr", "French"],
  ["de", "German"],
  ["it", "Italian"],
  ["pt", "Portuguese"],
  ["ru", "Russian"],
  ["uk", "Ukrainian"],
  ["pl", "Polish"],
  ["ro", "Romanian"],
  ["nl", "Dutch"],
  ["el", "Greek"],
];

/**
 * Extra spellings clinical vocabularies use that are not the roster's own
 * label or tag. Notably the written-Chinese codes: `zh` has no single spoken
 * form, and the rail needs a SPOKEN language, so `zh` resolves to Mandarin (the
 * overwhelmingly more common assumption) and `zh-HK`/`zh-yue` to Cantonese.
 * All keys are lowercase; lookups are lowercased before matching.
 */
const ALIASES: Readonly<Record<string, string>> = {
  // Chinese variants — written code → spoken tag.
  zh: "cmn",
  "zh-cn": "cmn",
  "zh-hans": "cmn",
  chinese: "cmn",
  "mandarin chinese": "cmn",
  "zh-hk": "yue",
  "zh-yue": "yue",
  "zh-hant": "yue",
  "cantonese chinese": "yue",
  // Endonyms / alternate English names seen in intake vocabularies.
  persian: "fa",
  farsi: "fa",
  filipino: "tl",
  pilipino: "tl",
  castilian: "es",
  espanol: "es",
  "español": "es",
  hebrew: "he",
  khmer: "km",
  cambodian: "km",
  // ISO 639-2/T three-letter forms that occasionally leak through.
  spa: "es",
  eng: "en",
  kor: "ko",
  vie: "vi",
  tgl: "tl",
  rus: "ru",
  ara: "ar",
  hye: "hy",
  arm: "hy",
  fas: "fa",
  per: "fa",
};

/** Lowercased tag → canonical tag, and lowercased label → canonical tag. */
const LOOKUP: ReadonlyMap<string, string> = new Map<string, string>([
  ...ROSTER.map(([code]) => [code.toLowerCase(), code] as const),
  ...ROSTER.map(([code, label]) => [label.toLowerCase(), code] as const),
  ...Object.entries(ALIASES),
]);

/**
 * Normalize care-gql's `preferred_language` into a BCP-47 tag present in the
 * translator's language roster.
 *
 * Matching, in order: exact (case-insensitive) tag or English label → known
 * alias → the primary subtag of a regional form (`es-MX` → `es`) → default.
 *
 * @param preferredLanguage raw `preferred_language` off the member record;
 *   null/undefined/blank are normal (most records have no value).
 * @returns a tag guaranteed to be in `TRANSLATOR_LANGUAGES`; `"es"` by default.
 */
export function toBcp47(preferredLanguage: string | null | undefined): string {
  const raw = preferredLanguage?.trim().toLowerCase();
  if (!raw) return DEFAULT_PATIENT_LANGUAGE;

  const direct = LOOKUP.get(raw);
  if (direct) return direct;

  // Regional / script-qualified forms: "es-MX", "pt_BR", "fr-CA". Try the
  // primary subtag alone. (Chinese regional forms are handled as explicit
  // aliases above, since `zh-HK` must NOT collapse to `zh` → Mandarin.)
  const primary = raw.split(/[-_]/)[0];
  if (primary && primary !== raw) {
    const byPrimary = LOOKUP.get(primary);
    if (byPrimary) return byPrimary;
  }

  return DEFAULT_PATIENT_LANGUAGE;
}
